import {Component, OnDestroy, OnInit} from '@angular/core';
import {WelcomePageActionsEnum} from '@core/enums/welcome-page-actions.enum';
import {Router} from '@angular/router';
import {BASIC_CONSENT, CMS_IMAGES, CMS_INDICATIONS, CONSENT, EXPORT, MEDICATION_MANAGEMENT, PATIENT_CONSENT_OVERVIEW,
    QUESTIONNAIRE, SETTINGS, SINGLE_COMMENT, SUBMITTED_CONSENTS, SUPER_ADMIN, USER_MANAGEMENT, WIZARD} from '@core/services/routes.service';
import {ConsentRestService} from '@core/modules/rest/consent/consent-rest.service';
import {getLanguageCodeByLanguageEnum, getLanguageEnumByLanguageCode, LanguageEnum} from '@core/enums/language.enum';
import {ResumeConsentModel} from '@core/modules/rest/consent/response/resume-consent.model';
import {ResumeStateEnum} from '@core/modules/rest/consent/enums/resume-state.enum';
import {AuthRestService} from '@core/modules/rest/authentication/auth-rest.service';
import {Observable} from 'rxjs';
import {LoggedUserDataModel} from '@core/modules/rest/user/response/logged-user-data.model';
import {CoreActions, CoreReducer} from '@core/store';
import {AppState} from '@core/reducers';
import {Store} from '@ngrx/store';
import {tap} from 'rxjs/operators';
import {UserSettingsModel} from '@core/modules/rest/user/response/user-settings.model';
import {KeycloakService} from 'keycloak-angular';
import {filterBayerRolesFromKeycloak} from '../../../../../../auth/keycloak-helper';
import {StartNewConsentPopupComponent} from '../components/popups/start-new-consent-popup/start-new-consent-popup.component';
import {MatDialog, MatDialogConfig, MatDialogRef} from '@angular/material/dialog';
import {WarningTwoOptionDialogModel} from '../components/popups/start-new-consent-popup/model/warning-two-option-dialog.model';
import {TranslateService} from '@ngx-translate/core';
import {TtsControlEnum} from '@core/enums/tts-control.enum';
import {TtsHelperService} from '@shared/utils/tts-helper.service';
import {TtsRestService} from '@core/modules/rest/text-to-speech/tts-rest.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ReadingSpeedEnum} from '@core/enums/reading-speed.enum';
import {ReadingContentTypeEnum} from '@core/enums/reading-content-type.enum';
import {PersonalSettingsModel} from '@shared/model/personal-settings.model';
import * as fromActions from '../../../../../../core/store/core.actions';
import {UserRestService} from '@core/modules/rest/user/user-rest.service';
import {KeycloakApplicationRolesEnum} from '@core/modules/rest/user/enums/keycloak-application-roles.enum';
import {LanguageStyleEnum} from '@core/enums/language-style.enum';
import {PresentationModeEnum} from '@core/enums/presentation-mode.enum';
import {WizardStepsEnum} from '@core/enums/wizard-steps.enum';
import {SubmittedInitialConsentModel} from '@core/modules/rest/consent/response/submitted-initial-consent.model';
import {WizardActions} from '../../wizard/store';

@UntilDestroy()
@Component({
    selector: 'app-welcome-container',
    templateUrl: './welcome-container.component.html',
    styleUrls: ['./welcome-container.component.scss']
})
export class WelcomeContainerComponent implements OnInit, OnDestroy {

    loggedUserInfo$: Observable<LoggedUserDataModel> = this.store.select(CoreReducer.loggedUserDataSelector);

    disableResume = true;
    resumeInfo: ResumeConsentModel | undefined;
    loggedUserSettings: UserSettingsModel | undefined;
    personalSettings: PersonalSettingsModel | undefined;
    hasConsumedConsents = false;
    roles: any;
    isAdmin = false;
    isDoctor = false;
    isPatient = false;
    isEditor = false;
    isSuperAdmin = false;

    ttsIconState = TtsControlEnum.playState;
    repeatButtonDisabled = true;
    showHeaderControls = true;
    audio = new Audio();
    ttsUrl: string | undefined;
    firstPlay = true;

    textToRead: string | undefined;
    autoTextRead = false;
    language: LanguageEnum;
    missingData: WizardStepsEnum[];

    constructor(private router: Router, private consentService: ConsentRestService, private authService: AuthRestService,
                private store: Store<AppState>, private keycloakService: KeycloakService, private dialog: MatDialog,
                private translate: TranslateService, private ttsHelperService: TtsHelperService, private ttsService: TtsRestService,
                private userService: UserRestService) {
    }

    ngOnInit(): void {
        this.handleUserRoles();
        this.store.dispatch(fromActions.GetLoggedUserData());
        this.language = getLanguageEnumByLanguageCode(localStorage.getItem('languageCode'));
        this.audio.addEventListener('ended', this.setButtonTextOnEnd, false);
        this.autoTextRead = sessionStorage.getItem('audioMode') === 'true';

        this.loggedUserInfo$.pipe(tap((loggedUserData) => {
            if (loggedUserData) {
                this.loggedUserSettings = JSON.parse(loggedUserData.settings);
                this.personalSettings = JSON.parse(loggedUserData.personalSettings);
                if (this.personalSettings !== null && this.personalSettings.language && this.language !== this.personalSettings.language) {
                    const langCode = getLanguageCodeByLanguageEnum(this.personalSettings.language);
                    localStorage.setItem('languageCode', langCode);
                    this.language = this.personalSettings.language;
                    this.translate.use(langCode);
                }

                const isSuperAdmin = loggedUserData.roles.indexOf(KeycloakApplicationRolesEnum.SUPERADMIN) > -1;
                if ((this.personalSettings !== null && !this.personalSettings.language) || (!this.personalSettings && !isSuperAdmin)) {
                    this.personalSettings = {
                        language: getLanguageEnumByLanguageCode(localStorage.getItem('languageCode')),
                        ...this.personalSettings
                    };
                    this.userService.updatePersonalSettings(JSON.stringify(this.personalSettings)).subscribe();
                }
            }
        })).subscribe();

        if (this.isPatient) {
            this.consentService.getStartedConsent(this.language).pipe(untilDestroyed(this)).subscribe(data => {
                if (data) {
                    if (data.hasConsentsToResume) {
                        this.disableResume = false;
                    }
                    this.resumeInfo = data;
                    this.hasConsumedConsents = data.hasConsumedConsents;
                    this.getTextToReadBasedOnUserConsentData();
                    if (this.autoTextRead) {
                        this.readText();
                    }
                }
            });
        }

    }

    setButtonTextOnEnd = (evt: { type: string; }) => {
        if (evt.type === 'ended') {
            this.ttsIconState = TtsControlEnum.playState;
        }
    }

    getTextToReadBasedOnUserConsentData(): void {
        this.textToRead = this.translate.instant('VOICE_OUTPUT_TEXTS.DASHBOARD.WELCOME');
        if (this.disableResume) {
            this.textToRead += this.translate.instant('VOICE_OUTPUT_TEXTS.DASHBOARD.NEW_CONSENT');
        } else {
            this.textToRead += this.translate.instant('VOICE_OUTPUT_TEXTS.DASHBOARD.RESUME');
        }
    }

    nextStep(action: WelcomePageActionsEnum): void {
        this.pause(false);
        switch (action) {
            case WelcomePageActionsEnum.NEW_CONSENT:
                if (this.disableResume) {
                    this.startNewConsentAction();
                } else {
                    this.openAvailableConsentToResumeWarning();
                }
                break;
            case WelcomePageActionsEnum.RESUME_CONSENT:
                if (this.resumeInfo.consentLanguage === this.language) {
                    this.resume();
                } else {
                    this.openResumeConsentOnDifferentLanguageWarning();
                }
                break;
            case WelcomePageActionsEnum.VIEW_CONSENTS:
                this.router.navigate([SUBMITTED_CONSENTS]);
                break;
            case WelcomePageActionsEnum.USER_MANAGEMENT:
                this.router.navigate([USER_MANAGEMENT]);
                break;
            case WelcomePageActionsEnum.EXPORT_DATA:
                this.router.navigate([EXPORT]);
                break;
            case WelcomePageActionsEnum.SETTINGS:
                this.router.navigate([SETTINGS]);
                break;
            case WelcomePageActionsEnum.CMS_INDICATIONS:
                this.router.navigate([CMS_INDICATIONS]);
                break;
            case WelcomePageActionsEnum.CMS_IMAGES:
                this.router.navigate([CMS_IMAGES]);
                break;
            case WelcomePageActionsEnum.ORGANIZATION_MANAGEMENT:
                this.router.navigate([SUPER_ADMIN]);
                break;
            case WelcomePageActionsEnum.MEDICATION_MANAGEMENT:
                this.router.navigate([MEDICATION_MANAGEMENT]);
                break;
            default:
        }
    }

    openConfirmationPopup(dialogInfo: WarningTwoOptionDialogModel): MatDialogRef<StartNewConsentPopupComponent> {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.data = dialogInfo;
        dialogConfig.autoFocus = true;
        dialogConfig.disableClose = true;
        return this.dialog.open(StartNewConsentPopupComponent, dialogConfig);
    }

    resume(): void {
        switch (this.resumeInfo.resumeStep) {
            case ResumeStateEnum.CONSENT:
                if (this.loggedUserSettings.basicMode) {
                    this.router.navigate([BASIC_CONSENT(this.resumeInfo.consentId)]);
                } else {
                    this.navigateToWizardOrConsent(CONSENT, this.resumeInfo.consentId);
                }

                break;
            case ResumeStateEnum.SINGLE_COMMENT:
                this.router.navigate([SINGLE_COMMENT], {queryParams: {consentId: this.resumeInfo.consentId}});
                break;
            case ResumeStateEnum.QUESTIONNAIRE:
                this.router.navigate([QUESTIONNAIRE(this.resumeInfo.consentId)]);
                break;
            case ResumeStateEnum.OVERVIEW:
                this.router.navigate([PATIENT_CONSENT_OVERVIEW(this.resumeInfo.consentId)]);
                break;
            case ResumeStateEnum.WIZARD:
                this.navigateToWizardOrConsent(WIZARD, this.resumeInfo.consentId);
                break;
            default:
                this.navigateToWizardOrConsent(WIZARD, this.resumeInfo.consentId);
        }
    }

    logout(): void {
        this.pause(false);
        this.authService.logout();
    }

    startNewConsentAndGoToWizard(): void {

        const missingData: WizardStepsEnum[] = [];
        if (this.personalSettings.languageStyle === LanguageStyleEnum.CUSTOM) {
            missingData.push(WizardStepsEnum.LANGUAGE_STYLE);
        }
        if (this.personalSettings.presentationMode === PresentationModeEnum.CUSTOM) {
            missingData.push(WizardStepsEnum.PRESENTATION_MODE);
        }
        if (this.personalSettings.indicationId === null) {
            missingData.push(WizardStepsEnum.INDICATIONS);
        }
        if (this.personalSettings.useCaseId === 'CUSTOM_USE_CASE') {
            missingData.push(WizardStepsEnum.USE_CASES);
        }

        const wizContent = {
            wizardStepEnum: missingData[0],
            language: this.language,
            missingData
        };

        this.consentService.submitInitialConsent(JSON.stringify(wizContent)).subscribe(consentId => {
            this.navigateToWizardOrConsent(WIZARD, consentId);
        });
    }

    startNewConsent(): void {
        this.consentService.submitInitialFromSettings().subscribe((submitted: SubmittedInitialConsentModel) => {
            this.store.dispatch(WizardActions.GetWizardContent({consentId: submitted.id}));
            this.navigateToWizardOrConsent(CONSENT, submitted.id);
        });
    }

    navigateToWizardOrConsent(route: string, consentId?: string): void {
        if (consentId) {
            this.router.navigate([route], {
                queryParams: {
                    consentId
                }
            });
        } else {
            this.router.navigate([route]);
        }
    }

    handleUserRoles(): void {
        this.roles = filterBayerRolesFromKeycloak(this.keycloakService.getUserRoles());
        if (this.roles.includes('patient')) {
            this.isPatient = true;
        }
        if (this.roles.includes('admin')) {
            this.isAdmin = true;
        }
        if (this.roles.includes('doctor')) {
            this.isDoctor = true;
        }
        if (this.roles.includes('contenteditor')) {
            this.isEditor = true;
        }
        if (this.roles.includes('superadmin')) {
            this.isSuperAdmin = true;
        }
    }

    startNewConsentAction(): void {
        if (this.loggedUserSettings?.basicMode) {
            this.consentService.submitInitialBasicConsent().subscribe(consentId => {
                this.router.navigate([BASIC_CONSENT(consentId)]);
            });
        } else {
            if (this.personalSettings.languageStyle === LanguageStyleEnum.CUSTOM || this.personalSettings.presentationMode === PresentationModeEnum.CUSTOM
                || !this.personalSettings.indicationId || this.personalSettings.useCaseId === 'CUSTOM_USE_CASE') {
                this.startNewConsentAndGoToWizard();
            } else {
                this.startNewConsent();
            }
        }
    }

    openAvailableConsentToResumeWarning(): void {
        const dialogData: WarningTwoOptionDialogModel = {
            title: this.translate.instant('ONBOARDING.WELCOME.START_NEW_POPUP_TITLE'),
            content: this.translate.instant('ONBOARDING.WELCOME.START_NEW_POPUP_TEXT'),
            icon: 'warning',
            iconColor: 'warning',
            leftButtonText: this.translate.instant('GENERAL.CANCEL'),
            rightButtonText: this.translate.instant('GENERAL.START_NEW')
        };
        const dialogRef = this.openConfirmationPopup(dialogData);
        dialogRef.componentInstance.closeDialogEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
            if (!this.audio.ended) {
                this.resumeAudio(false);
            }
        });
        dialogRef.componentInstance.confirmActionEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
            this.startNewConsentAction();
        });
    }

    openResumeConsentOnDifferentLanguageWarning(): void {
        let displayLanguage;
        if (this.resumeInfo.consentLanguage === LanguageEnum.GERMAN) {
            displayLanguage = this.translate.instant('GENERAL.GERMAN');
        } else if (this.resumeInfo.consentLanguage === LanguageEnum.SERBIAN) {
            displayLanguage = this.translate.instant('GENERAL.SERBIAN');
        } else {
            displayLanguage = this.translate.instant('GENERAL.ENGLISH');
        }
        const dialogData: WarningTwoOptionDialogModel = {
            title: this.translate.instant('ONBOARDING.WELCOME.CONTINUE_CONSENT_LANGUAGE_WARNING_TITLE', {language: displayLanguage}),
            content: this.translate.instant('ONBOARDING.WELCOME.CONTINUE_CONSENT_LANGUAGE_WARNING_TEXT', {language: displayLanguage}),
            icon: 'translate',
            iconColor: 'info',
            leftButtonText: this.translate.instant('GENERAL.CANCEL'),
            rightButtonText: this.translate.instant('GENERAL.CONTINUE')
        };
        const dialogRef = this.openConfirmationPopup(dialogData);
        dialogRef.componentInstance.closeDialogEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
        });
        dialogRef.componentInstance.confirmActionEvent.pipe(untilDestroyed(this)).subscribe(() => {
            dialogRef.close();
            this.changeLanguageAndContinue();
        });
    }

    changeLanguageAndContinue(): void {
        const language = this.resumeInfo.consentLanguage;
        const languageCode = getLanguageCodeByLanguageEnum(language);
        this.translate.use(languageCode);
        localStorage.setItem('languageCode', languageCode);
        this.personalSettings.language = language;
        this.userService.updatePersonalSettings(JSON.stringify(this.personalSettings)).subscribe(() => {
            this.resume();
        });
    }

    play(): void {
        this.audio.load();
        this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
    }

    readText(): void {
        if (this.firstPlay) {
            this.ttsIconState = TtsControlEnum.pauseState;
            this.convertTextToSpeech();
        } else {
            if (this.audio.ended) {
                this.play();
            } else {
                if (this.ttsIconState === TtsControlEnum.playState) {
                    this.resumeAudio(false);
                } else {
                    this.pause(false);
                }
            }
        }
    }

    convertTextToSpeech(): void {
        const finalText = this.ttsHelperService.removeSSML(this.textToRead);

        const pollyObject = {
            text: finalText,
            readingSpeed: ReadingSpeedEnum.MEDIUM,
            readingLanguage: this.language,
            readingContentType: ReadingContentTypeEnum.TEXT
        };

        this.firstPlay = false;
        this.repeatButtonDisabled = false;
        this.ttsService.getTTS(pollyObject).subscribe((data: any) => {
            this.ttsUrl = data;
            if (this.ttsUrl) {
                this.audio.src = this.ttsUrl;
                this.play();
            }
        });
    }

    pause(automatic: boolean): void {
        if (!automatic) {
            this.ttsIconState = TtsControlEnum.playState;
        }
        if (!this.audio.ended) {
            this.audio.pause();
        }
    }

    resumeAudio(automatic: boolean): void {
        if (!this.audio.ended) {
            if (this.ttsIconState === TtsControlEnum.pauseState && automatic) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
            if (this.ttsIconState === TtsControlEnum.playState && !automatic) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
        }
    }

    ngOnDestroy(): void {
        this.pause(false);
    }

}
